import { create } from 'zustand';
import {
  api,
  setSession,
  getToken,
  getRefreshToken,
  onRefreshFailure,
} from '../api/client';

export interface MeUser {
  id: string;
  email: string;
  username: string;
  referralCode: string;
  vRank: string;
  referrerId?: string | null;
  createdAt?: string;
}

interface AuthResponse {
  token: string;
  refreshToken: string;
  user: MeUser;
}

interface AuthState {
  user: MeUser | null;
  loading: boolean;
  error: string | null;
  bootstrap: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<void>;
  register: (args: { email: string; username: string; password: string; referralCode?: string }) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => {
  // If a refresh attempt fails mid-session, wipe the user from the store so
  // the app bounces back to the login screen instead of showing stale data.
  onRefreshFailure(() => {
    set({ user: null });
  });

  return {
    user: null,
    loading: false,
    error: null,

    async bootstrap() {
      if (!getToken() && !getRefreshToken()) return;
      set({ loading: true });
      try {
        const { user } = await api<{ user: MeUser }>('/auth/me');
        set({ user, loading: false });
      } catch {
        setSession(null, null);
        set({ user: null, loading: false });
      }
    },

    async login(identifier, password) {
      set({ loading: true, error: null });
      try {
        const res = await api<AuthResponse>('/auth/login', {
          method: 'POST',
          body: JSON.stringify({ identifier, password }),
        });
        setSession(res.token, res.refreshToken);
        set({ user: res.user, loading: false });
      } catch (e) {
        set({ error: (e as Error).message, loading: false });
        throw e;
      }
    },

    async register(args) {
      set({ loading: true, error: null });
      try {
        const res = await api<AuthResponse>('/auth/register', {
          method: 'POST',
          body: JSON.stringify(args),
        });
        setSession(res.token, res.refreshToken);
        set({ user: res.user, loading: false });
      } catch (e) {
        set({ error: (e as Error).message, loading: false });
        throw e;
      }
    },

    async logout() {
      // Clear local state immediately so the UI can route away without waiting
      // on the network call. Server-side revoke is best-effort in the background.
      const rt = getRefreshToken();
      setSession(null, null);
      set({ user: null });
      if (rt) {
        try {
          await fetch(
            (import.meta.env.VITE_API_BASE_URL ?? '/api/v1') + '/auth/logout',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken: rt }),
            },
          );
        } catch {
          // swallow — local state is already cleared
        }
      }
    },
  };
});
